import { convertValueToNumber } from './utils/utils.js';
import { getTrackPosForIndex } from './utils/metrics.js';

//  calculates widths exactly like your _calculateWidths()
//  provide viewportWidth (number), options (object), slideCount (number)
//  pass in convertValueToNumber so this stays pure and testable
//  boundsEl (optional) is the element used for slide visibility bounds (defaults to viewportEl)
export function calculateWidths({ viewportEl, options, slideCount, boundsEl }) {
	const viewport = viewportEl.offsetWidth || 0;

	// Visibility bounds (separate from layout viewport): used only for inert/aria-hidden math.
	// When boundsEl === viewportEl (the common case), offset is 0 and width matches viewport.
	const resolvedBoundsEl = boundsEl || viewportEl;
	let visibilityBoundsWidth = viewport;
	let visibilityBoundsOffset = 0;
	if (resolvedBoundsEl !== viewportEl) {
		const viewportRect = viewportEl.getBoundingClientRect();
		const boundsRect = resolvedBoundsEl.getBoundingClientRect();
		visibilityBoundsWidth = resolvedBoundsEl.offsetWidth || viewport;
		visibilityBoundsOffset = boundsRect.left - viewportRect.left;
	}

	//  guard inputs
	const slidesPerView = Math.max(1, Number(options.slidesPerView) || 1);

	//  gap and padding (responsive units resolved against viewport width)
	const gap = convertValueToNumber(options.gap, viewport);
	let paddingLeft = convertValueToNumber(options.paddingLeft, viewport);
	let paddingRight = convertValueToNumber(options.paddingRight, viewport);

	// Enforce minimum padding from effect rules
	if (options.rules) {
		const minLeft = convertValueToNumber(options.rules.minPaddingLeft, viewport);
		const minRight = convertValueToNumber(options.rules.minPaddingRight, viewport);
		if (paddingLeft < minLeft) paddingLeft = minLeft;
		if (paddingRight < minRight) paddingRight = minRight;
	}

	//  total widths to remove from viewport
	const totalPaddingWidth = paddingLeft + paddingRight;
	const totalGapWidth = gap * (slidesPerView - 1);

	//  slide width derived from the remaining space
	// totalSlideWidth = viewportWidth - totalGapWidth - totalPaddingWidth
	const totalSlideWidth = Math.max(0, viewport - totalGapWidth - totalPaddingWidth);
	let slide = slidesPerView > 0 ? Math.round((totalSlideWidth / slidesPerView) * 1000) / 1000 : 0;

	//  slide min width resolved against the computed slide width
	const slideMin = convertValueToNumber(options.slideMinWidth, slide);
	if (slide < slideMin) {
		slide = slideMin;
	}

	//  track width: contains gap at the end for looping
	const track = Math.max(0, slideCount) * (slide + gap);

	//  return a flat object that maps to _.widths.* plus track
	return {
		viewport,
		track,
		slide,
		slideMin,
		gap,
		slideAndGap: Math.round((slide + gap) * 1000) / 1000,
		paddingLeft,
		paddingRight,
		visibilityBoundsWidth,
		visibilityBoundsOffset,
	};
}

//  calculates page count based on navigation semantics
//  pageCount = number of valid page indices where pageIndex * slidesPerMove = valid slideIndex
export function calculatePageCount({
	loop,
	slidesPerMove,
	slidesPerView,
	slideCount,
	centerSelectedSlide,
}) {
	// no slides means no pages
	if (slideCount === 0) {
		return 0;
	}

	// if all slides are visible, only one page needed regardless of loop mode
	if (slideCount <= slidesPerView) {
		return 1;
	}

	// loop or centered mode: each slide (per slidesPerMove) is a page
	if (loop || centerSelectedSlide) {
		return Math.ceil(slideCount / slidesPerMove);
	}

	// non-looped left-aligned: count valid starting positions for the viewport
	// viewport can start at slideIndex 0 through (slideCount - slidesPerView)
	// since navigation uses pageIndex * slidesPerMove = slideIndex,
	// we need: ceil((validSlidePositions) / slidesPerMove)
	const validSlidePositions = slideCount - slidesPerView;
	return Math.ceil(validSlidePositions / slidesPerMove) + 1;
}

//  calculates transform points for effects positioning
//  provides named positioning points like L1, L2, C, R1, R2, etc.
//  extends all the way to L10 and R10 for maximum flexibility
export function calculateTransformPoints(ctx) {
	const { store } = ctx;
	const widths = store.getWidths();
	const options = store.getOptions();

	// base geometry
	const halfSlideWidth = widths.slide / 2;
	const stepDistance = widths.slideAndGap;

	// visible slide count (at least 1)
	const visibleSlides = options.slidesPerView;

	// CL1 = center of the leftmost visible slide in viewport
	// This is where the first slide's center is when at rest position
	const CL1 = widths.paddingLeft + halfSlideWidth;

	const points = { CL1 };

	// generate CL1..CLN (left → right across the viewport)
	// These are the center positions of each visible slide
	for (let i = 1; i <= visibleSlides; i++) {
		points[`CL${i}`] = CL1 + stepDistance * (i - 1);
	}

	// generate CR1..CRN (right → left mirror of CL)
	// CR1 = center of rightmost visible slide (CLN), CR2 = CL(N-1), ...
	for (let i = 1; i <= visibleSlides; i++) {
		const mirroredIndex = visibleSlides - i + 1; // N, N-1, ..., 1
		points[`CR${i}`] = points[`CL${mirroredIndex}`];
	}

	// add center point (C) - average of all visible slide positions
	if (visibleSlides === 1) {
		points.C = points.CL1;
	} else {
		// center is the midpoint between first and last visible slides
		points.C = (points.CL1 + points[`CL${visibleSlides}`]) / 2;
	}

	// generate L1..L10 to the left of CL1
	for (let i = 1; i <= 10; i++) {
		points[`L${i}`] = CL1 - stepDistance * i;
	}

	// generate R1..R10 to the right of the rightmost visible slide
	const lastVisiblePos = points[`CL${visibleSlides}`];
	for (let i = 1; i <= 10; i++) {
		points[`R${i}`] = lastVisiblePos + stepDistance * i;
	}

	return points;
}

/**
 * Calculate snap points (track positions) for each page
 * @param {Object} widths - Width measurements from store
 * @param {Object} options - Carousel options from store
 * @param {Object} state - Carousel state from store
 * @returns {Object} { snapPoints: number[], firstSnapPoint: number, lastSnapPoint: number }
 */
export function calculateSnapPoints(widths, options, state) {
	const { pageCount } = state;
	const { slidesPerMove } = options;

	const snapPoints = [];

	for (let page = 0; page < pageCount; page++) {
		const slideIndex = page * slidesPerMove;
		snapPoints.push(getTrackPosForIndex(slideIndex, widths, options, state));
	}

	return {
		snapPoints,
		firstSnapPoint: snapPoints[0] ?? 0,
		lastSnapPoint: snapPoints[snapPoints.length - 1] ?? 0,
	};
}
